import { inject, Injectable } from "@angular/core";
import { SearchCriteriaType } from "../../pojo/SearchCriteriaI";
import { ItemOptions, ItemStore } from "./item.store";
import {
    ContactItemStore,
    EventItemStore,
    LocationItemStore,
    OrganizationItemStore,
    ResourceItemStore,
    StudentEngagementOrganizationsItemStore,
} from "./s25.item.store";
import {
    AssignedEventStatesStore,
    BudgetPlanTypesStore,
    BudgetsStore,
    BuildingsStore,
    ContactCustomAttributesStore,
    ElectionPositionsForElectionStore,
    ElectionPositionsStore,
    ElectionsStore,
    EmailTemplatesStore,
    EventCabinetsStore,
    EventCategoriesStore,
    EventCustomAttributesStore,
    EventFoldersStore,
    EventRequirementsStore,
    EventRolesStore,
    EventSearchesStore,
    EventStatesStore,
    EventTypeReportsStore,
    EventTypesStore,
    FiscalYearsStore,
    FormConfigsStore,
    ImagesStore,
    IngestionFieldsAllStore,
    IngestionPointsStore,
    LocationCategoriesStore,
    LocationCustomAttributesStore,
    LocationFeaturesStore,
    LocationLayoutsStore,
    LocationPartitionsStore,
    LocationRelatedTypesStore,
    LocationSearchesStore,
    ManualEmailTemplatesStore,
    MatchGroupsStore,
    MatchQuestionsAllStore,
    MatchQuestionsStore,
    NonTextMatchQuestionsStore,
    OrganizationCategoriesStore,
    OrganizationCustomAttributesStore,
    OrganizationRatingsStore,
    OrganizationRolesStore,
    OrganizationSearchesStore,
    OrganizationTypesStore,
    ResourceCategoriesStore,
    ResourceCustomAttributesStore,
    ResourceSearchesStore,
    ResourcesQuantityStore,
    SecurityGroupCategoriesStore,
    SecurityGroupsStore,
    StandardSchedulesStore,
    SystemTagsStore,
    TaxSchedulesStore,
    TodoTemplatesStore,
    WorkflowProcessesStore,
} from "./search.criteria.item.store";
import { Item } from "../../pojo/Item";
import { IdItem, ItemStoreID, FetchConfig, ItemCriteria, StoredItem } from "./item.store.internal";

@Injectable({ providedIn: "root" })
export class ItemService {
    readonly events = inject(EventItemStore);
    readonly organizations = inject(OrganizationItemStore);
    readonly contacts = inject(ContactItemStore);
    readonly locations = inject(LocationItemStore);
    readonly resources = inject(ResourceItemStore);

    private stores = {
        securityGroups: inject(SecurityGroupsStore),
        securityGroupCategories: inject(SecurityGroupCategoriesStore),
        assignedEventStates: inject(AssignedEventStatesStore),
        eventStates: inject(EventStatesStore),
        eventTypes: inject(EventTypesStore),
        eventFolders: inject(EventFoldersStore),
        eventCabinets: inject(EventCabinetsStore),
        events: this.events,
        eventSearches: inject(EventSearchesStore),
        eventCategories: inject(EventCategoriesStore),
        eventRequirements: inject(EventRequirementsStore),
        eventRoles: inject(EventRolesStore),
        eventCustomAttributes: inject(EventCustomAttributesStore),
        eventTypeReports: inject(EventTypeReportsStore),
        electionPositions: inject(ElectionPositionsStore),
        electionPositionsForElection: inject(ElectionPositionsForElectionStore),
        elections: inject(ElectionsStore),
        locationRelatedTypes: inject(LocationRelatedTypesStore),
        locationSearches: inject(LocationSearchesStore),
        locations: this.locations,
        locationFeatures: inject(LocationFeaturesStore),
        locationCategories: inject(LocationCategoriesStore),
        locationPartitions: inject(LocationPartitionsStore),
        locationLayouts: inject(LocationLayoutsStore),
        locationCustomAttributes: inject(LocationCustomAttributesStore),
        resourceSearches: inject(ResourceSearchesStore),
        resources: this.resources,
        resourcesQuantity: inject(ResourcesQuantityStore),
        resourceCategories: inject(ResourceCategoriesStore),
        resourceCustomAttributes: inject(ResourceCustomAttributesStore),
        organizations: this.organizations,
        studentEngagementOrganizations: inject(StudentEngagementOrganizationsItemStore),
        organizationSearches: inject(OrganizationSearchesStore),
        organizationTypes: inject(OrganizationTypesStore),
        organizationCategories: inject(OrganizationCategoriesStore),
        organizationRatings: inject(OrganizationRatingsStore),
        organizationCustomAttributes: inject(OrganizationCustomAttributesStore),
        organizationRoles: inject(OrganizationRolesStore),
        contacts: this.contacts,
        contactCustomAttributes: inject(ContactCustomAttributesStore),
        emailTemplates: inject(EmailTemplatesStore),
        manualEmailTemplates: inject(ManualEmailTemplatesStore),
        todoTemplates: inject(TodoTemplatesStore),
        systemTags: inject(SystemTagsStore),
        buildings: inject(BuildingsStore),
        images: inject(ImagesStore),
        taxSchedules: inject(TaxSchedulesStore),
        standardSchedules: inject(StandardSchedulesStore),
        matchQuestions: inject(MatchQuestionsStore),
        nonTextMatchQuestions: inject(NonTextMatchQuestionsStore),
        matchQuestionsAll: inject(MatchQuestionsAllStore),
        matchGroups: inject(MatchGroupsStore),
        ingestionPoints: inject(IngestionPointsStore),
        ingestionFieldsAll: inject(IngestionFieldsAllStore),
        workflowProcesses: inject(WorkflowProcessesStore),
        formConfigs: inject(FormConfigsStore),
        budgets: inject(BudgetsStore),
        budgetPlanTypes: inject(BudgetPlanTypesStore),
        fiscalYears: inject(FiscalYearsStore),
    } as const satisfies Record<SearchCriteriaType["type"], ItemStore<any>>;

    private byType: Partial<Record<Item.Ids, ItemStore<Item.Ids>>> = {
        [Item.Ids.Event]: this.events,
        [Item.Ids.Organization]: this.organizations,
        [Item.Ids.Contact]: this.contacts,
        [Item.Ids.Location]: this.locations,
        [Item.Ids.Resource]: this.resources,
        [Item.Ids.Report]: this.stores.eventTypeReports,
        [Item.Ids.EventType]: this.stores.eventTypes,
        // [Item.Ids.RateSchedule]: this.rateSchedules
        // [Item.Ids.Requirement]: this.stores.eventRequirements,
        // [Item.Ids.Task]: this.tasks
        // [Item.Ids.ConflictBlock]: this.conflictBlocks
    };

    get(item: Item<Item.Ids>, options?: ItemOptions): StoredItem<Item.Ids> {
        return this.byType[item.itemTypeId]!.get(item, options);
    }

    getAll(items: Item<Item.Ids>[], options?: ItemOptions): StoredItem<Item.Ids>[] {
        return items.map((item) => this.get(item, options));
    }

    fetch(item: Item<Item.Ids>, options?: ItemOptions): Promise<Item<Item.Ids>> {
        return this.byType[item.itemTypeId]!.fetch(item, options);
    }

    storeItem(item: Item<Item.Ids>, fetched: FetchConfig): Item<Item.Ids> {
        return this.byType[item.itemTypeId]!.storeItem(item, fetched);
    }

    storeAllItems(items: Item<Item.Ids>[], fetched: FetchConfig): Item<Item.Ids>[] {
        return items.map((item) => this.storeItem(item, fetched));
    }

    criteria(type: SearchCriteriaType["type"]): ItemStore<any> {
        const store = this.stores[type];
        if (!store) throw new Error(`No item store for criteria type "${type}"`);
        return store;
    }
}
